import { useCallback, useEffect, useState } from "react";
import { Badge, Button, Collapse } from "react-bootstrap";
import GameTypeBadge from "../../GameTypeBadge";
import { Controller, ShareFill } from "react-bootstrap-icons";
import { useTranslation } from "react-i18next";
import { createGameSession, deleteGameSession, listGameSessions, updateGameSession } from "../../../functions/gameSession";
import GameSessionEditor from "./edit/GameSessionEditor";
import GameSessionShareOffcanvas from "./edit/GameSessionShareOffcanvas";
import SuccessModal from "../../SuccessModal";
import PageLayout from "../../PageLayout";

function GameSessionsDisplayer({ token, onError }) {
    const { t } = useTranslation('messages');
    const [gameSessions, setGameSessions] = useState([]);
    const [showEditor, setShowEditor] = useState(false);
    const [editorSession, setEditorSession] = useState(null);
    const [editorExternalId, setEditorExternalId] = useState(null);
    const [editorGameType, setEditorGameType] = useState(null);
    const [expandedSessions, setExpandedSessions] = useState(new Set());
    const [shareSessionId, setShareSessionId] = useState(null);
    const [shareSessionName, setShareSessionName] = useState(null);
    const [showCreatedToast, setShowCreatedToast] = useState(false);

    const loadSessions = useCallback(() => {
        listGameSessions(token, (data) => setGameSessions(data.gameSessions || []), onError);
    }, [token, onError]);

    useEffect(() => { loadSessions(); }, [loadSessions]);

    function toggleExpanded(sessionId) {
        setExpandedSessions(prev => {
            const next = new Set(prev);
            if (next.has(sessionId)) next.delete(sessionId);
            else next.add(sessionId);
            return next;
        });
    }

    function onEditSession(session) {
        setEditorSession(session);
        setEditorExternalId(null);
        setShowEditor(true);
    }

    function onNewSessionSameGame(session) {
        setEditorSession(null);
        setEditorExternalId(session.externalId);
        setEditorGameType(session.gameType);
        setShowEditor(true);
    }

    function onDeleteSession(gameSessionId) {
        deleteGameSession(token, () => loadSessions(), onError, gameSessionId);
    }

    function onOpenShare(session) {
        setShareSessionId(session.gameSessionId);
        setShareSessionName(session.name);
    }

    function onCloseShare() {
        setShareSessionId(null);
        setShareSessionName(null);
    }

    function onSave(payload) {
        if (payload.gameSessionId) {
            updateGameSession(token, () => loadSessions(), onError, payload);
        } else {
            createGameSession(token, () => { loadSessions(); setShowCreatedToast(true); }, onError, payload);
        }
    }

    function onClose() {
        setShowEditor(false);
        setEditorSession(null);
        setEditorExternalId(null);
        setEditorGameType(null);
    }

    return (
        <>
            <PageLayout
                hero={<h1 className="display-5 fw-bold mb-0">{t('game_sessions.title')}</h1>}
            >
                {gameSessions.length === 0 ? (
                    <div className="text-center py-5 text-muted">
                        <Controller size={48} className="mb-3 opacity-25" />
                        <p className="mb-0">{t('game_sessions.empty_state')}</p>
                    </div>
                ) : (
                    <div>
                        {gameSessions.map((session, index) => {
                            const isExpanded = expandedSessions.has(session.gameSessionId);
                            return (
                                <div key={index} className="card border-0 shadow-sm mb-3">
                                    <div
                                        className="card-body p-3 d-flex gap-3 align-items-center"
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => toggleExpanded(session.gameSessionId)}
                                    >
                                        {session.imageLink && (
                                            <img
                                                src={session.imageLink}
                                                alt={session.name}
                                                className="rounded flex-shrink-0"
                                                style={{ width: 56, height: 56, objectFit: 'cover' }}
                                            />
                                        )}
                                        <div className="flex-grow-1 min-w-0">
                                            <div className="d-flex justify-content-between align-items-center flex-wrap gap-1">
                                                <div className="d-flex align-items-center gap-2">
                                                    <span className="fw-semibold text-dark">{session.name}</span>
                                                    <GameTypeBadge gameType={session.gameType} />
                                                </div>
                                                {session.playedAt && (
                                                    <span className="text-muted small">
                                                        {new Date(session.playedAt).toLocaleString()}
                                                    </span>
                                                )}
                                            </div>
                                            {session.players && session.players.length > 0 && (
                                                <div className="d-flex flex-wrap gap-1 mt-1">
                                                    {session.players.map((player, i) => (
                                                        <Badge key={i} bg={player.winner ? 'success' : 'secondary'}>
                                                            {player.name}
                                                        </Badge>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <Collapse in={isExpanded}>
                                        <div>
                                            <div className="card-body pt-0 px-3 pb-3 border-top">
                                                {session.note && (
                                                    <p className="text-muted small mb-2 mt-2">{session.note}</p>
                                                )}
                                                {session.durationMinutes && (
                                                    <p className="text-muted small mb-2">
                                                        {t('game_sessions.column_duration')}: {session.durationMinutes} min
                                                    </p>
                                                )}
                                                <div className="d-flex flex-wrap gap-2 mt-2">
                                                    <Button size="sm" variant="outline-primary"
                                                        onClick={(e) => { e.stopPropagation(); onEditSession(session); }}>
                                                        {t('game_sessions.edit_button')}
                                                    </Button>
                                                    <Button size="sm" variant="outline-danger"
                                                        onClick={(e) => { e.stopPropagation(); onDeleteSession(session.gameSessionId); }}>
                                                        {t('game_sessions.delete_button')}
                                                    </Button>
                                                    <Button size="sm" variant="outline-secondary"
                                                        onClick={(e) => { e.stopPropagation(); onNewSessionSameGame(session); }}>
                                                        {t('game_sessions.new_same_game_button')}
                                                    </Button>
                                                    <Button size="sm" variant="outline-secondary"
                                                        onClick={(e) => { e.stopPropagation(); onOpenShare(session); }}>
                                                        <ShareFill size={12} className="me-1" />
                                                        {t('game_sessions.share_button')}
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                    </Collapse>
                                </div>
                            );
                        })}
                    </div>
                )}
            </PageLayout>

            <GameSessionEditor
                show={showEditor}
                onClose={onClose}
                session={editorSession}
                externalId={editorExternalId}
                gameType={editorGameType}
                onSave={onSave}
            />
            <SuccessModal
                show={showCreatedToast}
                message={t('game_sessions.created_toast')}
                onClose={() => setShowCreatedToast(false)}
            />
            <GameSessionShareOffcanvas
                show={shareSessionId !== null}
                onClose={onCloseShare}
                sessionId={shareSessionId}
                sessionName={shareSessionName}
                token={token}
                onError={onError}
                onLeave={(data) => {
                    setGameSessions(data.gameSessions || []);
                    onCloseShare();
                }}
            />
        </>
    );
}

export default GameSessionsDisplayer;
